import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import {
  Search, Send, RefreshCw, MessageSquare, ChevronLeft,
  User as UserIcon, Clock, CheckCircle2, AlertCircle, Loader2,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import {
  ADMIN_SUPPORT_TICKETS,
  ADMIN_SUPPORT_TICKET_DETAILS,
  ADMIN_SUPPORT_REPLY,
  ADMIN_SUPPORT_SET_STATUS,
} from "../../api/services/adminSupportService";
import { getErrorMessage } from "@/utils/errorUtils";

type TicketListItem = {
  _id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  user: { _id: string; name: string; email: string; profileImage?: string } | null;
  messageCount: number;
  lastActivityAt: string;
  createdAt: string;
};

type TicketDetailMessage = {
  senderRole: string;
  sender: { firstName?: string; lastName?: string; email?: string } | null;
  content: string;
  isInternal?: boolean;
  createdAt: string;
};

type TicketDetail = {
  _id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  userId: { firstName?: string; lastName?: string; email?: string } | null;
  messages: TicketDetailMessage[];
  createdAt: string;
};

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "waiting_for_user", label: "Waiting" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

const STATUS_ACTIONS = [
  { key: "in_progress", label: "Mark In Progress" },
  { key: "resolved", label: "Mark Resolved" },
  { key: "closed", label: "Close Ticket" },
  { key: "open", label: "Reopen" },
];

const statusColor = (status: string, isDark: boolean) => {
  const map: Record<string, string> = {
    open: isDark ? "bg-blue-900/40 text-blue-300" : "bg-blue-100 text-blue-700",
    pending: isDark ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700",
    in_progress: isDark ? "bg-purple-900/40 text-purple-300" : "bg-purple-100 text-purple-700",
    waiting_for_user: isDark ? "bg-orange-900/40 text-orange-300" : "bg-orange-100 text-orange-700",
    resolved: isDark ? "bg-green-900/40 text-green-300" : "bg-green-100 text-green-700",
    closed: isDark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600",
  };
  return map[status] || (isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600");
};

const priorityColor = (p: string, isDark: boolean) => {
  const map: Record<string, string> = {
    urgent: "text-red-500",
    high: "text-orange-500",
    medium: isDark ? "text-gray-400" : "text-gray-500",
    low: isDark ? "text-gray-500" : "text-gray-400",
  };
  return map[p] || (isDark ? "text-gray-400" : "text-gray-500");
};



export default function AdminSupport() {
  const { t } = useTranslation();
  const { theme } = useApp();
  const isDark = theme === "dark";

  const fmtTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = Date.now();
    const diff = now - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('admin.support.time.justNow', 'just now');
    if (mins < 60) return t('admin.support.time.minsAgo', '{{count}}m ago', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('admin.support.time.hrsAgo', '{{count}}h ago', { count: hrs });
    const days = Math.floor(hrs / 24);
    if (days < 7) return t('admin.support.time.daysAgo', '{{count}}d ago', { count: days });
    return d.toLocaleDateString();
  };

  const prettyStatus = (s: string) => {
    const defaultLabel = s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return t(`admin.support.status.${s}`, defaultLabel);
  };

  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [counts, setCounts] = useState<Record<string, number>>({});

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [toast, setToast] = useState<{ msg: string; error?: boolean } | null>(null);

  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const showToast = (msg: string, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3000);
  };

  const loadTickets = async () => {
    setLoadingList(true);
    try {
      const res = await ADMIN_SUPPORT_TICKETS({ status: statusFilter, search, limit: 50 });
      if ((res as unknown as Record<string, unknown>)?.success) {
        setTickets(((res as unknown as Record<string, unknown>).data || []) as TicketListItem[]);
        setCounts(((res as unknown as Record<string, unknown>).statusCounts || {}) as Record<string, number>);
      }
    } catch (e: unknown) {
      console.error("load tickets error", e);
      showToast(t('admin.support.toast.loadFailed', 'Failed to load tickets'), true);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  const openTicket = async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const res = await ADMIN_SUPPORT_TICKET_DETAILS(id);
      if ((res as unknown as Record<string, unknown>)?.success) setDetail((res as unknown as Record<string, unknown>).data as TicketDetail);
    } catch (e: unknown) {
      console.error("load detail error", e);
      showToast(t('admin.support.toast.loadTicketFailed', 'Failed to load ticket'), true);
    } finally {
      setLoadingDetail(false);
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedId) return;
    setSending(true);
    try {
      const res = await ADMIN_SUPPORT_REPLY(selectedId, reply.trim());
      if ((res as unknown as Record<string, unknown>)?.success) {
        setDetail((res as unknown as Record<string, unknown>).data as TicketDetail);
        setReply("");
        showToast(t('admin.support.toast.replySent', 'Reply sent'));
        loadTickets();
        setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        showToast(getErrorMessage(res) || t('admin.support.toast.sendFailed', 'Failed to send'), true);
      }
    } catch (e: unknown) {
      showToast(getErrorMessage(e) || t('admin.support.toast.replyFailed', 'Failed to send reply'), true);
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status: string) => {
    if (!selectedId) return;
    setUpdatingStatus(true);
    try {
      const res = await ADMIN_SUPPORT_SET_STATUS(selectedId, status);
      if ((res as unknown as Record<string, unknown>)?.success) {
        setDetail((res as unknown as Record<string, unknown>).data as TicketDetail);
        showToast(`${t('admin.support.toast.status', 'Status:')} ${prettyStatus(status)}`);
        loadTickets();
      } else {
        showToast(getErrorMessage(res) || t('admin.support.toast.statusFailed', 'Failed to update status'), true);
      }
    } catch (e: unknown) {
      showToast(getErrorMessage(e) || t('admin.support.toast.statusFailed', 'Failed to update status'), true);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const cardBg = isDark ? "bg-[#1a1b1d] border-gray-800" : "bg-white border-gray-200";
  const textMain = isDark ? "text-white" : "text-gray-900";
  const textSub = isDark ? "text-gray-400" : "text-gray-600";
  const textMuted = isDark ? "text-gray-500" : "text-gray-400";

  return (
    <div className="relative">
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium flex items-center gap-2 ${toast.error ? "bg-red-600" : "bg-green-600"
            }`}
        >
          {toast.error ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5">
        {/* ===== LEFT: ticket list ===== */}
        <Card className={`${cardBg} p-0 overflow-hidden ${selectedId ? "hidden lg:block" : ""}`}>
          <div className={`p-4 border-b ${isDark ? "border-gray-800" : "border-gray-200"}`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-lg font-semibold ${textMain}`}>{t('admin.support.title', 'Support Tickets')}</h2>
              <button
                onClick={loadTickets}
                className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}

              >
                <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput.trim()); }}
                placeholder={t('admin.support.searchPlaceholder', 'Search subject...')}
                className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none border ${isDark ? "bg-[#222] border-gray-700 text-white placeholder-gray-500" : "bg-gray-50 border-gray-200 text-gray-900"
                  }`}
              />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${statusFilter === tab.key
                      ? "bg-blue-600 text-white"
                      : isDark ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {t(`admin.support.filter.${tab.key}`, tab.label)}
                  {counts[tab.key] != null && <span className="ml-1 opacity-70">{counts[tab.key]}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
            {loadingList ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : tickets.length === 0 ? (
              <div className={`text-center py-16 ${textMuted}`}>
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">{t('admin.support.noTickets', 'No tickets here')}</p>
              </div>
            ) : (
              tickets.map((ticket) => (
                <button
                  key={ticket._id}
                  onClick={() => openTicket(ticket._id)}
                  className={`w-full text-left p-4 border-b transition-colors ${isDark ? "border-gray-800 hover:bg-gray-800/50" : "border-gray-100 hover:bg-gray-50"
                    } ${selectedId === ticket._id ? (isDark ? "bg-gray-800/70" : "bg-blue-50") : ""}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className={`font-medium text-sm truncate ${textMain}`}>{ticket.subject}</span>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor(ticket.status, isDark)}`}>
                      {prettyStatus(ticket.status)}
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${textSub}`}>
                    <UserIcon className="w-3 h-3" />
                    <span className="truncate">{ticket.user?.name || t('admin.support.unknown', 'Unknown')}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[11px] ${textMuted}`}>{ticket.category} · <span className={priorityColor(ticket.priority, isDark)}>{ticket.priority}</span></span>
                    <span className={`text-[11px] ${textMuted} flex items-center gap-1`}>
                      <Clock className="w-3 h-3" />{fmtTime(ticket.lastActivityAt)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* ===== RIGHT: thread ===== */}
        <Card className={`${cardBg} p-0 overflow-hidden flex flex-col ${!selectedId ? "hidden lg:flex" : ""}`}>
          {!selectedId ? (
            <div className={`flex-1 flex flex-col items-center justify-center py-24 ${textMuted}`}>
              <MessageSquare className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">{t('admin.support.selectTicket', 'Select a ticket to view the conversation')}</p>
            </div>
          ) : loadingDetail ? (
            <div className="flex-1 flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : detail ? (
            <>
              {/* header */}
              <div className={`p-4 border-b ${isDark ? "border-gray-800" : "border-gray-200"}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => setSelectedId(null)}
                    className={`lg:hidden p-1.5 rounded-lg ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-semibold ${textMain}`}>{detail?.subject}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor(detail?.status, isDark)}`}>
                        {prettyStatus(detail?.status)}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 ${textSub}`}>
                      {detail?.userId ? `${[detail?.userId.firstName, detail?.userId.lastName].filter(Boolean).join(" ")} · ${detail?.userId.email}` : ""}
                      {" · "}{detail?.category} · <span className={priorityColor(detail?.priority, isDark)}>{detail?.priority}</span>
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {STATUS_ACTIONS.map((a) => (
                    <button
                      key={a.key}
                      disabled={updatingStatus || detail?.status === a.key}
                      onClick={() => changeStatus(a.key)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 ${isDark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      {t(`admin.support.action.${a.key}`, a.label)}
                    </button>
                  ))}
                </div>
              </div>

              {/* thread */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[calc(100vh-420px)]">
                {(detail?.messages || []).map((m: Record<string, unknown>, i: number) => {
                  const isUser = m.senderRole === "user";
                  const senderName = m.sender
                    ? [((m.sender || {}) as unknown as Record<string, unknown>).firstName, ((m.sender || {}) as unknown as Record<string, unknown>).lastName].filter(Boolean).join(" ")
                    : isUser ? t('admin.support.role.user', 'User') : t('admin.support.role.support', 'Support');
                  return (
                    <div key={i} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${isUser
                          ? (isDark ? "bg-gray-800 text-gray-100" : "bg-gray-100 text-gray-900")
                          : "bg-blue-600 text-white"
                        } ${m.isInternal ? "ring-2 ring-amber-400" : ""}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[11px] font-semibold opacity-90">
                            {isUser ? senderName : (m.senderRole === "admin" ? t('admin.support.role.supportAdmin', 'Support (Admin)') : t('admin.support.role.support', 'Support'))}
                          </span>
                          {m.isInternal && <span className="text-[9px] bg-amber-400 text-black px-1.5 rounded">{t('admin.support.role.internal', 'INTERNAL')}</span>}
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.content as string}</p>
                        <span className="block text-[10px] opacity-60 mt-1">{fmtTime(m.sentAt as string)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEndRef} />
              </div>

              {/* reply box */}
              {detail?.status === "closed" ? (
                <div className={`p-4 border-t text-center text-sm ${isDark ? "border-gray-800 text-gray-500" : "border-gray-200 text-gray-400"}`}>
                  {t('admin.support.ticketClosed', 'This ticket is closed. Reopen it to reply.')}
                </div>
              ) : (
                <div className={`p-3 border-t ${isDark ? "border-gray-800" : "border-gray-200"}`}>
                  <div className="flex items-end gap-2">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                      placeholder={t('admin.support.replyPlaceholder', 'Type your reply to the user... (Cmd/Ctrl+Enter to send)')}
                      rows={2}
                      className={`flex-1 resize-none rounded-xl px-3 py-2 text-sm outline-none border ${isDark ? "bg-[#222] border-gray-700 text-white placeholder-gray-500" : "bg-gray-50 border-gray-200 text-gray-900"
                        }`}
                    />
                    <button
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm font-medium"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {t('admin.support.send', 'Send')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={`flex-1 flex items-center justify-center py-24 ${textMuted}`}>
              <p className="text-sm">{t('admin.support.ticketNotFound', 'Ticket not found')}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
